import * as React from "react";
import { fetchTrafficMetaViaFind } from "../api/lineParentStops.fromFind";
import type { TrafficMeta } from "@ogt/ui";

function findFirstStringByKeyRegex(
  obj: unknown,
  keyRegex: RegExp,
  maxDepth = 4
): string | undefined {
  if (!obj || typeof obj !== "object" || maxDepth < 0) return undefined;
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    if (keyRegex.test(k) && typeof v === "string") return v;
  }
  for (const [, v] of Object.entries(obj as Record<string, unknown>)) {
    const found = findFirstStringByKeyRegex(v, keyRegex, maxDepth - 1);
    if (found) return found;
  }
  return undefined;
}
const pickIconCss = (line: any) =>
  line?.Ogt?.OgtTrafficTypeIconCssClass ??
  line?.LineType?.OgtTrafficTypeIconCssClass ??
  line?.OgtTrafficTypeIconCssClass ??
  findFirstStringByKeyRegex(line, /TrafficType.*Icon.*CssClass/i, 5);

const pickGroupCss = (line: any) =>
  line?.Ogt?.OgtTrafficTypeGroupCssClass ??
  line?.LineType?.OgtTrafficTypeGroupCssClass ??
  line?.OgtTrafficTypeGroupCssClass ??
  findFirstStringByKeyRegex(line, /TrafficType.*Group.*CssClass/i, 5);

export type UseTrafficMetaOptions = {
  fetcher?: (
    lineId: string
  ) => Promise<{ iconCss?: string; groupCss?: string }>;
};

export function useTrafficMeta(line: any, opts?: UseTrafficMetaOptions) {
  const localIcon = React.useMemo(() => pickIconCss(line), [line]);
  const localGroup = React.useMemo(() => pickGroupCss(line), [line]);

  const [meta, setMeta] = React.useState<TrafficMeta | undefined>(() =>
    localIcon || localGroup
      ? { iconCss: localIcon, groupCss: localGroup, title: localIcon }
      : undefined
  );
  const [loading, setLoading] = React.useState<boolean>(
    !Boolean(localIcon && localGroup)
  );
  const [error, setError] = React.useState<unknown>(null);

  React.useEffect(() => {
    let alive = true;

    if (localIcon && localGroup) {
      setMeta({
        iconCss: localIcon,
        groupCss: localGroup,
        title: localIcon ?? undefined,
      });
      setLoading(false);
      setError(null);
      return;
    }

    const lineId = String(line?.id ?? line?.LineNrReal ?? "");
    if (!lineId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    (async () => {
      try {
        const fetcher = opts?.fetcher ?? fetchTrafficMetaViaFind;
        const { iconCss, groupCss } = await fetcher(lineId);
        if (!alive) return;

        setMeta({
          iconCss: iconCss ?? localIcon,
          groupCss: groupCss ?? localGroup,
          title: iconCss ?? localIcon ?? undefined,
        });
        setError(null);
      } catch (e) {
        if (!alive) return;
        setMeta(
          (prev) =>
            prev ??
            (localIcon || localGroup
              ? { iconCss: localIcon, groupCss: localGroup }
              : undefined)
        );
        setError(e);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [line, localIcon, localGroup, opts?.fetcher]);

  return { meta, loading, error };
}
